import type { GBufferData } from "./types.ts";

export interface ReadbackTile {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const READBACK_TILE_SIZE = 1024;

let pooledGBuffer: GBufferData | null = null;

function createGBuffer(width: number, height: number): GBufferData {
  const pixelCount = width * height;
  return {
    width,
    height,
    depth: new Float32Array(pixelCount),
    normals: new Float32Array(pixelCount * 3),
    objectIds: new Uint32Array(pixelCount),
    luminance: new Float32Array(pixelCount),
    resolvedAutoSmoothAngles: new Float32Array(pixelCount),
  };
}

function isReusableGBuffer(gbuffer: GBufferData): boolean {
  const pixelCount = gbuffer.width * gbuffer.height;
  return (
    gbuffer.depth.length === pixelCount &&
    gbuffer.normals.length === pixelCount * 3 &&
    gbuffer.objectIds.length === pixelCount &&
    gbuffer.luminance.length === pixelCount &&
    gbuffer.resolvedAutoSmoothAngles.length === pixelCount
  );
}

export function takeReusableGBuffer(
  width: number,
  height: number,
): GBufferData {
  if (
    pooledGBuffer &&
    pooledGBuffer.width === width &&
    pooledGBuffer.height === height
  ) {
    const reused = pooledGBuffer;
    pooledGBuffer = null;
    return reused;
  }
  return createGBuffer(width, height);
}

export function recycleGBuffer(gbuffer: GBufferData | null | undefined): void {
  if (!gbuffer || !isReusableGBuffer(gbuffer)) return;
  pooledGBuffer = gbuffer;
}

export function disposeReusableGBufferPool(): void {
  pooledGBuffer = null;
}

export function listReadbackTiles(
  width: number,
  height: number,
  tileSize = READBACK_TILE_SIZE,
): ReadbackTile[] {
  const tiles: ReadbackTile[] = [];
  for (let y = 0; y < height; y += tileSize) {
    const tileHeight = Math.min(tileSize, height - y);
    for (let x = 0; x < width; x += tileSize) {
      tiles.push({
        x,
        y,
        width: Math.min(tileSize, width - x),
        height: tileHeight,
      });
    }
  }
  return tiles;
}
